import React, { useEffect, useState } from "react";
import { Plus, Pencil, Trash2, Save, X, Loader2 } from "lucide-react";
import * as LucideIcons from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Header } from "@/pages/admin/AdminDashboard";
import { Card, Field } from "@/pages/admin/AdminProfile";

const empty = { name: "", tagline: "", description: "", icon: "Code2", price_range: "", deliverables: [], featured: false, order: 0 };

export default function AdminServices() {
    const [items, setItems] = useState([]);
    const [editing, setEditing] = useState(null);
    const [loading, setLoading] = useState(true);

    const load = async () => {
        setLoading(true);
        setItems((await api.get("/services")).data);
        setLoading(false);
    };
    useEffect(() => { load(); }, []);

    const remove = async (id) => {
        if (!window.confirm("Delete this service?")) return;
        await api.delete(`/admin/services/${id}`);
        toast.success("Service deleted");
        load();
    };

    return (
        <div data-testid="admin-services-page">
            <Header
                title="Services"
                subtitle="Layanan yang Anda tawarkan ke client. Ditampilkan di section 'How I can help'."
                action={
                    <button onClick={() => setEditing({ ...empty })} data-testid="service-new" className="inline-flex items-center gap-2 px-5 py-2.5 rounded-md bg-brand text-white hover:bg-brand-hover">
                        <Plus size={16} /> New service
                    </button>
                }
            />
            {loading ? <div className="mono-label">Loading…</div> : (
                <div className="grid sm:grid-cols-2 gap-3">
                    {items.map((s) => {
                        const Cmp = LucideIcons[s.icon] || LucideIcons.Code2;
                        return (
                            <div key={s.id} className="p-5 rounded-xl border border-white/5 bg-ink-800/40 flex items-start gap-4">
                                <div className="w-10 h-10 rounded-lg bg-brand/15 border border-brand/30 flex items-center justify-center text-brand flex-shrink-0">
                                    <Cmp size={18} />
                                </div>
                                <div className="min-w-0 flex-1">
                                    <div className="flex items-center gap-2">
                                        <span className="text-white font-medium truncate">{s.name}</span>
                                        {s.featured && <span className="font-mono text-[10px] px-1.5 py-0.5 rounded bg-brand/15 text-brand">Featured</span>}
                                    </div>
                                    <div className="text-xs text-ink-200 truncate">{s.tagline}</div>
                                    {s.price_range && <div className="mt-1 mono-label">{s.price_range}</div>}
                                </div>
                                <div className="flex gap-1 flex-shrink-0">
                                    <button onClick={() => setEditing({ ...s })} className="p-2 rounded hover:bg-white/10 text-ink-200 hover:text-white"><Pencil size={14} /></button>
                                    <button onClick={() => remove(s.id)} className="p-2 rounded hover:bg-red-500/10 text-ink-200 hover:text-red-400"><Trash2 size={14} /></button>
                                </div>
                            </div>
                        );
                    })}
                    {items.length === 0 && <div className="col-span-full p-12 text-center text-ink-300 border border-dashed border-white/10 rounded-xl">No services yet.</div>}
                </div>
            )}
            {editing && <Editor initial={editing} onClose={() => setEditing(null)} onSaved={load} />}
        </div>
    );
}

function Editor({ initial, onClose, onSaved }) {
    const [data, setData] = useState(initial);
    const [saving, setSaving] = useState(false);
    const isEdit = Boolean(initial.id);
    const u = (k) => (e) => {
        const v = e.target.type === "checkbox" ? e.target.checked : e.target.type === "number" ? Number(e.target.value) : e.target.value;
        setData((s) => ({ ...s, [k]: v }));
    };
    const uLines = (k) => (e) => setData((s) => ({ ...s, [k]: e.target.value.split("\n").map((x) => x.trim()).filter(Boolean) }));

    const save = async () => {
        setSaving(true);
        try {
            if (isEdit) await api.put(`/admin/services/${initial.id}`, data);
            else await api.post("/admin/services", data);
            toast.success(isEdit ? "Service updated" : "Service created");
            onSaved();
            onClose();
        } catch (err) {
            toast.error(formatApiError(err.response?.data?.detail));
        } finally {
            setSaving(false);
        }
    };

    return (
        <div className="fixed inset-0 z-50 flex items-stretch justify-end bg-black/70 backdrop-blur" data-testid="service-editor">
            <div className="w-full max-w-xl bg-ink-900 border-l border-white/10 overflow-y-auto">
                <div className="sticky top-0 z-10 bg-ink-900/95 backdrop-blur border-b border-white/5 px-6 py-4 flex items-center justify-between">
                    <div>
                        <div className="mono-label">{isEdit ? "Edit service" : "New service"}</div>
                        <h2 className="font-display text-xl text-white">{data.name || "Untitled"}</h2>
                    </div>
                    <div className="flex gap-2">
                        <button onClick={save} disabled={saving} data-testid="service-save" className="inline-flex items-center gap-2 px-4 py-2 rounded-md bg-brand text-white hover:bg-brand-hover">
                            {saving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />} Save
                        </button>
                        <button onClick={onClose} className="p-2 rounded-md hover:bg-white/10 text-ink-200"><X size={16} /></button>
                    </div>
                </div>
                <div className="p-6 space-y-5">
                    <Card title="Service details">
                        <Field label="Name"><input className="ainp" value={data.name} onChange={u("name")} /></Field>
                        <Field label="Tagline (short one-liner)"><input className="ainp" value={data.tagline} onChange={u("tagline")} /></Field>
                        <Field label="Description"><textarea rows={4} className="ainp" value={data.description} onChange={u("description")} /></Field>
                        <div className="grid grid-cols-2 gap-3">
                            <Field label="Icon (lucide-react name)"><input className="ainp" value={data.icon} onChange={u("icon")} placeholder="Code2" /></Field>
                            <Field label="Price range"><input className="ainp" value={data.price_range} onChange={u("price_range")} placeholder="Mulai 15 jt" /></Field>
                        </div>
                        <Field label="Deliverables (one per line)">
                            <textarea rows={5} className="ainp" value={(data.deliverables || []).join("\n")} onChange={uLines("deliverables")} placeholder="UI/UX implementation&#10;Backend code&#10;30 hari support" />
                        </Field>
                        <div className="grid grid-cols-2 gap-3">
                            <label className="flex items-center gap-3 cursor-pointer pt-6">
                                <input type="checkbox" checked={data.featured} onChange={u("featured")} className="w-4 h-4 accent-brand" />
                                <span className="text-sm text-ink-100">Featured (Most popular badge)</span>
                            </label>
                            <Field label="Order"><input type="number" className="ainp" value={data.order} onChange={u("order")} /></Field>
                        </div>
                    </Card>
                </div>
            </div>
        </div>
    );
}
